import { useState, useEffect } from 'react'
import { Sparkles } from 'lucide-react'
import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'
import { SECTIONS } from '@/data/sections.data'

export interface AiProcessField {
  label: string
  value: string
  aiValue: string
  source?: string
  confidence?: number
  sectionId?: string
  /** When true the field is mandatory — Apply is blocked until a value is selected. */
  required?: boolean
  /** When present, renders this field as an interactive <select> in the value column. */
  editOptions?: string[]
  /** Fires when the user changes the select value for an editable field. */
  onEditChange?: (value: string) => void
}

interface AiProcessModalProps {
  open: boolean
  title?: string
  fields: AiProcessField[]
  onContinue: () => void
  onCancel: () => void
  hideConfidence?: boolean
  applyLabel?: string
  /** When provided, renders an amber warning banner below the modal header using the given message key. */
  warningKey?: string
  /** Pre-resolved (already-interpolated) warning banner text — use when the message needs
   * runtime params that `warningKey`'s plain `getMessage(key)` lookup can't supply. Takes
   * precedence over `warningKey` when both are given. */
  warningText?: string
  /** When provided, renders a blue info banner below the modal header using the given message key. */
  infoKey?: string
  /** When provided and fields list is empty, replaces the default empty-state description with this message key. */
  emptyMessageKey?: string
  /** When true, the apply/continue button is shown even when the fields list is empty. */
  showApplyWhenEmpty?: boolean,
  readOnly?: boolean
}

// Derive section order and display names from the canonical SECTIONS list.
const SECTION_META: Record<string, { order: number; name: string }> = {}
SECTIONS.forEach((s, i) => { SECTION_META[s.id] = { order: i, name: s.name } })

function confidenceColorClass(confidence: number): string {
  const pct = confidence * 100
  if (pct < 70) return 'text-red-600 font-bold'
  if (pct < 90) return 'text-yellow-600 font-bold'
  return 'text-green-600 font-bold'
}

function groupFields(fields: AiProcessField[]): Array<{ sectionId: string; name: string; items: AiProcessField[] }> {
  const map: Record<string, AiProcessField[]> = {}
  for (const f of fields) {
    const key = f.sectionId ?? '__none__'
    if (!map[key]) map[key] = []
    map[key].push(f)
  }

  return Object.keys(map)
    .sort((a, b) => {
      const oa = SECTION_META[a]?.order ?? 999
      const ob = SECTION_META[b]?.order ?? 999
      return oa - ob
    })
    .map((key) => ({
      sectionId: key,
      name: SECTION_META[key]?.name ?? 'Other',
      items: map[key],
    }))
}

export function AiProcessModal({
  open,
  title = getMessage('ocr.modal.ai_results_title'),
  fields,
  onContinue,
  onCancel,
  hideConfidence = false,
  applyLabel = getMessage('ai_modal.button_apply'),
  warningKey,
  warningText,
  infoKey,
  emptyMessageKey,
  showApplyWhenEmpty = false,
  readOnly = false
}: AiProcessModalProps) {
  // Track current values for editable fields (keyed by field label).
  // Initialised / reset whenever the fields prop changes (new OCR result).
  const [editValues, setEditValues] = useState<Record<string, string>>({})

  useEffect(() => {
    setEditValues(
      Object.fromEntries(
        fields.filter((f) => f.editOptions).map((f) => [f.label, f.aiValue])
      )
    )
  }, [fields])

  if (!open) return null

  const isEmpty = fields.length === 0
  const globalSource = fields.find((f) => f.source)?.source
  const groups = groupFields(fields)
  const multiSection = groups.length >= 1

  // Apply is blocked when any required editable field has no value selected.
  const canApply = !fields.some(
    (f) => f.required && f.editOptions && !(editValues[f.label] || f.aiValue)
  )

  const gridCols = hideConfidence ? 'grid-cols-[1fr_1fr]' : 'grid-cols-[1fr_1fr_72px]'

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      <div
        className={cn(
          'relative z-10 w-full mx-3 sm:mx-4 bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] overflow-hidden shadow-2xl max-h-[90dvh] flex flex-col',
          isEmpty ? 'sm:max-w-[520px]' : 'sm:max-w-[660px]'
        )}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-[8px] flex items-center justify-center flex-shrink-0 border"
              style={{ backgroundColor: 'rgba(61,142,240,0.1)', borderColor: 'rgba(61,142,240,0.4)', color: 'var(--accent)' }}
            >
              <Sparkles size={14} strokeWidth={2} className="sm:hidden" />
              <Sparkles size={16} strokeWidth={2} className="hidden sm:block" />
            </div>
            <div className="min-w-0">
              <p className="text-[15px] sm:text-[14px] font-bold truncate">{title}</p>
              <p className="text-[11px] sm:text-[10px] text-[var(--text-muted)]">
                {isEmpty ? getMessage('ai_modal.subtitle_empty') : getMessage('ai_modal.subtitle_results')}
              </p>
            </div>
          </div>

          {globalSource && (
            <span className="text-[11px] sm:text-[13px] font-bold text-green-700 bg-green-50 border border-green-200 px-2 sm:px-3 py-[3px] sm:py-[5px] rounded-[6px] flex-shrink-0">
              {getMessage('ai_modal.source_prefix')} {globalSource}
            </span>
          )}
        </div>

        {(warningText || warningKey) && (
          <div className="flex items-start gap-2 px-4 sm:px-5 py-[10px] bg-amber-50 border-b border-amber-200 flex-shrink-0">
            <span className="text-amber-500 text-[14px] flex-shrink-0 mt-[1px]">⚠</span>
            <p className="text-[12px] text-amber-800 font-medium leading-snug">
              {warningText ?? getMessage(warningKey as Parameters<typeof getMessage>[0])}
            </p>
          </div>
        )}

        {infoKey && (
          <div className="flex items-start gap-2 px-4 sm:px-5 py-[10px] bg-blue-50 border-b border-blue-200 flex-shrink-0">
            <span className="text-blue-500 text-[14px] flex-shrink-0 mt-[1px]">ℹ</span>
            <p className="text-[12px] text-blue-800 font-medium leading-snug">
              {getMessage(infoKey as Parameters<typeof getMessage>[0])}
            </p>
          </div>
        )}

        {isEmpty ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 flex-1">
            <div className="w-12 h-12 rounded-full bg-[var(--surface2)] border border-[var(--border)] flex items-center justify-center">
              <Sparkles size={22} strokeWidth={1.5} className="text-[var(--text-muted)]" />
            </div>
            <p className="text-[13px] font-semibold text-[var(--text)]">{getMessage('ai_modal.empty_title')}</p>
            <p className="text-[11px] text-[var(--text-muted)] text-center max-w-[320px]">
              {emptyMessageKey ? getMessage(emptyMessageKey as Parameters<typeof getMessage>[0]) : getMessage('ai_modal.empty_desc')}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop column header */}
            <div className="hidden sm:block flex-shrink-0 border-b border-[var(--border)] bg-[var(--surface2)]">
              <div className={cn('grid gap-3 px-5 py-2', gridCols)}>
                <span className="text-[10px] font-semibold text-[var(--text-dim)] uppercase tracking-wide">
                  {getMessage('ai_modal.col_field')}
                </span>
                <span className="text-[10px] font-semibold text-[var(--text-dim)] uppercase tracking-wide">
                  {getMessage('ai_modal.col_value')}
                </span>
                {!hideConfidence && (
                  <span className="text-[10px] font-semibold text-[var(--text-dim)] uppercase tracking-wide text-right">
                    {getMessage('ai_modal.col_confidence')}
                  </span>
                )}
              </div>
            </div>

            {/* Mobile column header */}
            <div className="sm:hidden flex justify-between px-4 py-2 border-b border-[var(--border)] bg-[var(--surface2)] flex-shrink-0">
              <span className="text-[12px] font-semibold text-[var(--text-dim)] uppercase tracking-wide">
                {getMessage('ai_modal.col_field')}
              </span>
              <span className="text-[12px] font-semibold text-[var(--text-dim)] uppercase tracking-wide">
                {getMessage('ai_modal.col_ai_value')}
              </span>
            </div>

            {/* Body — grouped by section */}
            <div className="overflow-y-auto flex-1 min-h-0">
              {groups.map((group, gi) => (
                <div key={group.sectionId}>
                  {/* Section heading row — only shown when there are multiple sections */}
                  {multiSection && (
                    <div className={cn(
                      'px-4 sm:px-5 py-[5px] bg-[var(--surface2)] border-b border-[var(--border)]',
                      gi > 0 && 'border-t border-[var(--border)]'
                    )}>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--accent)]">
                        {group.name}
                      </span>
                    </div>
                  )}

                  {/* Field rows */}
                  <div className="px-4 sm:px-5">
                    {group.items.map((field) => {
                      const { label, aiValue, confidence, required, editOptions, onEditChange } = field
                      const isEditable = !!editOptions
                      // For editable fields: current display value from local state, falling back to aiValue
                      const selectValue = isEditable ? (editValues[label] ?? aiValue) : aiValue

                      return (
                        <div
                          key={label}
                          className="border-b border-[var(--border)] last:border-0"
                        >
                          {/* Mobile row */}
                          <div className="sm:hidden flex items-start justify-between gap-3 py-[10px]">
                            <span className="text-[13px] font-medium text-[var(--text-dim)] flex-1 min-w-0">
                              {label}
                              {required && <span className="text-[var(--error)] ml-[2px]">*</span>}
                            </span>
                            <div className="flex flex-col items-end gap-[3px] flex-shrink-0">
                              {isEditable ? (
                                <select
                                  value={selectValue}
                                  onChange={(e) => {
                                    const v = e.target.value
                                    setEditValues((prev) => ({ ...prev, [label]: v }))
                                    onEditChange?.(v)
                                  }}
                                  className={cn('field-input', required && !selectValue && 'error')}
                                >
                                  <option value="">Select</option>
                                  {editOptions.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  className={cn(
                                    'text-[13px] font-mono whitespace-pre-wrap',
                                    aiValue === 'NA'
                                      ? 'text-[var(--text-muted)] italic'
                                      : 'text-[var(--text)] font-semibold'
                                  )}
                                >
                                  {aiValue}
                                </span>
                              )}
                              {!isEditable && confidence !== undefined && !hideConfidence && (
                                <span className={cn('text-[12px]', confidenceColorClass(confidence))}>
                                  {(confidence * 100).toFixed(0)}%
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Desktop row */}
                          <div className={cn('hidden sm:grid gap-3 py-[8px] items-center', gridCols)}>
                            <span className="text-[12px] font-medium text-[var(--text-dim)]">
                              {label}
                              {required && <span className="text-[var(--error)] ml-[2px]">*</span>}
                            </span>
                            {isEditable ? (
                              <select
                                value={selectValue}
                                onChange={(e) => {
                                  const v = e.target.value
                                  setEditValues((prev) => ({ ...prev, [label]: v }))
                                  onEditChange?.(v)
                                }}
                                className={cn('field-input', required && !selectValue && 'error')}
                              >
                                <option value="">— Select —</option>
                                {editOptions.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : (
                              <span
                                className={cn(
                                  'text-[12px] font-mono whitespace-pre-wrap',
                                  aiValue === 'NA'
                                    ? 'text-[var(--text-muted)] italic'
                                    : 'text-[var(--text)] font-semibold'
                                )}
                              >
                                {aiValue}
                              </span>
                            )}
                            {!hideConfidence && (
                              <span
                                className={cn(
                                  'text-[12px] text-right',
                                  !isEditable && confidence !== undefined
                                    ? confidenceColorClass(confidence)
                                    : 'text-[var(--text-muted)]'
                                )}
                              >
                                {!isEditable && confidence !== undefined
                                  ? `${(confidence * 100).toFixed(0)}%`
                                  : '—'}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <div className={cn(
          'flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4 border-t border-[var(--border)] bg-[var(--surface2)] flex-shrink-0',
          isEmpty && !showApplyWhenEmpty ? 'justify-end' : 'justify-between'
        )}>
          <button
            onClick={onCancel}
            className={cn(
              'flex items-center gap-[7px] px-4 py-2 rounded-[var(--radius-sm)]',
              'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
              'text-[var(--accent)] text-[12px] font-medium cursor-pointer',
              'hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]',
              'transition-all duration-[180ms]'
            )}
          >
            {getMessage('common.button.cancel')}
          </button>

          {(!isEmpty || showApplyWhenEmpty) && !readOnly && (
            <button
              onClick={onContinue}
              disabled={!canApply}
              className={cn(
                'flex items-center gap-2 px-4 sm:px-5 py-[9px] rounded-[var(--radius-sm)]',
                'text-white text-[12px] font-medium',
                'transition-all duration-[180ms] border border-transparent',
                canApply ? 'hover:opacity-90 cursor-pointer' : 'opacity-50 cursor-not-allowed'
              )}
              style={{ background: 'linear-gradient(135deg, var(--accent), #2563eb)' }}
            >
              {applyLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}